import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { LETTERS, LETTER_ORDER } from "@/data/letters";
import { Lightbulb, Play, Lock } from "lucide-react";

export default function PracticePage() {
  const { progress, refreshProgress } = useApp();
  const navigate = useNavigate();
  useEffect(() => { refreshProgress && refreshProgress(); }, [refreshProgress]);

  const needsPractice = progress.filter((p) => p.unlocked && !p.mastered && p.attempts > 0 && p.best_score < 80);
  const available = LETTER_ORDER.map((l) => progress.find((p) => p.letter === l)).filter((p) => p?.unlocked);

  return (
    <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6 sm:py-10">
      <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-[#1F2937]">Practice</h1>
      <p className="mt-2 font-body text-[#6B7280]">Sharpen letters you've already unlocked.</p>

      {needsPractice.length > 0 && (
        <div className="mt-6 rounded-3xl border-2 border-[#F59E0B]/40 bg-[#F59E0B]/5 p-6" data-testid="recommended-practice">
          <div className="flex items-center gap-2 text-[#B45309]">
            <Lightbulb size={20} />
            <h2 className="font-heading text-lg font-bold">Recommended Practice</h2>
          </div>
          <div className="mt-4 flex flex-wrap gap-3">
            {needsPractice.map((p) => (
              <button
                key={p.letter}
                onClick={() => navigate(`/lesson/${p.letter}`)}
                data-testid={`practice-rec-${p.letter}`}
                className="inline-flex items-center gap-2 rounded-2xl bg-white border border-[#E5DECF] px-4 py-3 font-bold text-[#1F2937] transition-all hover:border-[#F59E0B] hover:shadow-md active:translate-y-0.5"
              >
                <span className="font-cursive text-3xl text-[#0D9488]">{p.letter}</span>
                Practice lowercase {p.letter} again
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="mt-8 grid gap-4 sm:grid-cols-3">
        {LETTER_ORDER.map((letter) => {
          const p = progress.find((x) => x.letter === letter);
          const locked = !p?.unlocked;
          return (
            <button
              key={letter}
              disabled={locked}
              onClick={() => navigate(`/lesson/${letter}`)}
              data-testid={`practice-letter-${letter}`}
              className={`flex flex-col items-center rounded-3xl border border-[#E5DECF] p-6 transition-all ${
                locked ? "bg-[#F2ECE1]/60 cursor-not-allowed" : "bg-white hover:shadow-lg hover:-translate-y-1"
              }`}
            >
              <span className="font-cursive text-6xl text-[#0D9488]" style={{ opacity: locked ? 0.35 : 1 }}>{letter}</span>
              <span className="mt-2 font-heading font-bold text-[#1F2937]">{LETTERS[letter].displayName}</span>
              <span className={`mt-3 inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold ${locked ? "bg-[#94A3B8]/20 text-[#64748B]" : "bg-[#0D9488] text-white"}`}>
                {locked ? <><Lock size={14} /> Locked</> : <><Play size={14} /> Practice</>}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
